/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Cheques del cliente en la ficha (Fase C): responde "¿qué cheques me dio este
 * cliente y en qué estado están?". Las acciones (depositar/endosar/rechazar/
 * devolver) reusan el mismo detalle de Caja → Cartera, gated por módulo Checks.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { formatIsoDate, formatLocalDate } from '@shared/vueltoDateTime';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet';
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { useEffectiveBusinessCapabilities } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { captureCatch, reportUserVisibleFailure } from '../../shared/sentry/sentryUser';
import { showError, showSuccess } from '../../shared/ui/appToast';
import {
  applyCheckCommand,
  checkDueBadge,
  checkDueBadgeLabel,
  CHECK_STATUS_LABELS,
  listChecksForCustomer,
  type CheckRow,
} from '../../cash/infra-api';
import type { CheckActionSubmit, CheckActionSubmitParams } from '../../cash/ui-api';

// `CheckDetailActions` es lazy-require para cortar el ciclo cash↔customers
// (cash/screens importa customers/public-api para adjuntos y nombres).
const CheckDetailActions = (props: {
  check: CheckRow | null;
  todayIso: string;
  customerName: string | null;
  canEdit: boolean;
  canEndorse: boolean;
  canReject: boolean;
  submitting: boolean;
  submit: CheckActionSubmit;
  onClose: () => void;
}) => {
  const C = require('../../cash/ui-api').CheckDetailActions;
  return <C {...props} />;
};

type Props = {
  customerId: string;
  customerName: string;
  formatMoney: (n: number) => string;
  mobile?: boolean;
  /** Cambia cuando la ficha se refresca tras un movimiento → recarga la lista. */
  reloadToken?: string | number | null;
};

export function CustomerChecksSection({ customerId, customerName, formatMoney, mobile = false, reloadToken }: Props) {
  const capabilities = useEffectiveBusinessCapabilities();
  const enabled = capabilities.has('checks');
  const { canView, canEdit } = useBranchPermission('Checks');
  const canEndorse = useModulePermission('Checks', 'endorse');
  const canReject = useModulePermission('Checks', 'reject');
  const { flags } = useGlobalFeatureFlags();
  const expensesModuleEnabled = isGlobalFeatureOn(flags, 'expenses_module');

  const [items, setItems] = useState<CheckRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState<CheckRow | null>(null);
  const todayIso = useMemo(() => formatIsoDate(new Date()), []);

  const reload = useCallback(async () => {
    if (!enabled || !canView || !customerId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      setItems(await listChecksForCustomer({ customerId }));
    } catch (err) {
      captureCatch('customers', 'checks.list')(err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [enabled, canView, customerId, reloadToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const submit = useCallback<CheckActionSubmit>(
    async (params: CheckActionSubmitParams) => {
      if (submitting) return false;
      setSubmitting(true);
      try {
        await applyCheckCommand({
          operation: params.operation,
          checkId: params.checkId,
          settlementId: params.settlementId,
          expenseId: params.expenseId,
          reason: params.reason,
          note: params.note,
          operationId: `check-${params.operation}-${params.checkId}-${Date.now()}`,
        });
        showSuccess(params.successMessage);
        await reload();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        reportUserVisibleFailure({ module: 'customers', op: `checks.${params.operation}`, error: err });
        showError(message);
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, reload],
  );

  if (!enabled || !canView) return null;

  return (
    <PaperSheet
      style={mobile ? styles.panelMobile : styles.panelWide}
      testID="customers.detail.checks"
    >
      <View style={styles.headerRow}>
        <Ionicons name="card-outline" size={18} color={ticketColors.ink} />
        <Text style={styles.headerTitle}>Cheques</Text>
        {items.length > 0 ? <Text style={styles.headerCount}>{items.length}</Text> : null}
      </View>

      {loading && items.length === 0 ? (
        <ModalStatePanel loading loadingMessage="Cargando cheques…" />
      ) : items.length === 0 ? (
        <Text style={styles.emptyText}>
          Este cliente todavía no entregó cheques.
        </Text>
      ) : (
        <View>
          {items.map((check, index) => {
            const badge = check.status === 'in_portfolio' ? checkDueBadge(check.dueDate, todayIso) : null;
            return (
              <TouchableOpacity
                key={check.id}
                style={[styles.row, index === items.length - 1 && styles.rowLast]}
                onPress={() => setDetail(check)}
                accessibilityRole="button"
                accessibilityLabel={`Cheque ${check.number}`}
                testID={`customers.detail.checks.row.${check.id}`}
              >
                <View style={styles.rowMain}>
                  <View style={styles.rowTitleLine}>
                    <Text style={modalExperience.listRowTitle} numberOfLines={1}>
                      N° {check.number}
                    </Text>
                    {badge ? (
                      <View
                        style={[
                          styles.badge,
                          badge === 'overdue'
                            ? styles.badgeBad
                            : badge === 'due_today'
                              ? styles.badgeWarn
                              : styles.badgeNeutral,
                        ]}
                      >
                        <Text
                          style={[
                            styles.badgeText,
                            badge === 'overdue'
                              ? styles.badgeTextBad
                              : badge === 'due_today'
                                ? styles.badgeTextWarn
                                : styles.badgeTextNeutral,
                          ]}
                        >
                          {checkDueBadgeLabel(badge, check.dueDate, todayIso)}
                        </Text>
                      </View>
                    ) : null}
                    <Text style={styles.statusText}>{CHECK_STATUS_LABELS[check.status]}</Text>
                  </View>
                  <Text style={modalExperience.listMeta} numberOfLines={1}>
                    {check.drawer ?? 'Sin librador'}
                    {check.bank ? ` · ${check.bank}` : ''}
                    {check.dueDate ? ` · vence ${formatLocalDate(check.dueDate)}` : ''}
                    {check.sourceType === 'customer_payment' ? ' · cobro de cuenta' : ' · venta'}
                  </Text>
                </View>
                <Text style={styles.rowAmount}>{formatMoney(check.amount)}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}

      <CheckDetailActions
        check={detail}
        todayIso={todayIso}
        customerName={customerName}
        canEdit={canEdit}
        canEndorse={canEndorse && expensesModuleEnabled}
        canReject={canReject}
        submitting={submitting}
        submit={submit}
        onClose={() => setDetail(null)}
      />
    </PaperSheet>
  );
}

const styles = StyleSheet.create({
  panelWide: {
    gap: spacing.sm,
    padding: spacing.md,
  },
  panelMobile: {
    gap: spacing.sm,
    padding: spacing.sm,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  headerTitle: {
    ...typography.title3,
    color: ticketColors.ink,
    flex: 1,
  },
  headerCount: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
  },
  emptyText: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.rule,
  },
  rowLast: {
    borderBottomWidth: 0,
  },
  rowMain: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  rowTitleLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  rowAmount: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  badge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  badgeBad: {
    backgroundColor: '#fdecec',
  },
  badgeWarn: {
    backgroundColor: '#fff4d6',
  },
  badgeNeutral: {
    backgroundColor: ticketColors.paper2,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: '700',
  },
  badgeTextBad: {
    color: ticketColors.bad,
  },
  badgeTextWarn: {
    color: '#9a6a00',
  },
  badgeTextNeutral: {
    color: ticketColors.ink2,
  },
  statusText: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
});
